import { ArrowDownTrayIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { TotalsRow } from 'components/TableTotals'
import { ledgerMoney } from 'components/TrustLedgerGrid'
import { baseUrl } from 'config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTrustLedgerAll } from 'services'
import { Input2, Pagination } from 'stories/components'
import { formatDate } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { TRUST_LEDGER_CATEGORY_COLORS } from './constants'
import { ITrustLedgerAllRow } from './type'

/** Grid columns, and the sort keys the backend accepts for each. */
const COLUMNS: Array<{ key: string; title: string; align?: string }> = [
  { key: 'category', title: 'Category' },
  { key: 'trustAccountName', title: 'Trust Account' },
  { key: 'date', title: 'Date' },
  { key: 'type', title: 'Type' },
  { key: 'reference', title: 'Reference' },
  { key: 'fromWhom', title: 'From Whom Received/Paid' },
  { key: 'payment', title: 'Payment', align: 'text-right' },
  { key: 'deposit', title: 'Deposit', align: 'text-right' },
  { key: 'fees', title: 'Fees', align: 'text-right' },
  { key: 'balance', title: 'Balance', align: 'text-right' },
]

/**
 * Read-only roll-up of every Trust Ledger transaction across all tabs.
 *
 * Search, sort and paging all run on the backend. That matters for correctness as much as for
 * size: Balance is a running total computed over the whole ledger in date order per bucket, so
 * the server filters and sorts only after that walk. Paging in the client would have shown a
 * balance column that only made sense on page one.
 */
export default function AllTransactionsTab() {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<ITrustLedgerAllRow[]>([])
  const [categories, setCategories] = useState<Array<{ key: string; label: string }>>([])
  const [totalCount, setTotalCount] = useState(0)
  // Summed server-side over the whole filtered ledger: this grid only ever holds one page.
  const [totals, setTotals] = useState<{ payment: number; deposit: number; fees: number } | null>(null)

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [orderBy, setOrderBy] = useState('date')
  // Newest transaction first: on a ledger of any age the recent rows are the ones being looked at.
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc')
  const [pageNum, setPageNum] = useState(0)
  const [itemCountPerPage, setItemCountPerPage] = useState(25)

  const token = useSelector((state: any) => state.auth.token)
  const loanNumber = setLoanNumber()

  // Typing should not fire a request per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const firstRender = useRef(true)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
      // A new search invalidates the page the user was on.
      if (!firstRender.current) setPageNum(0)
    }, 350)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    firstRender.current = false
    setLoading(true)
    getTrustLedgerAll({
      query: debouncedQuery,
      category,
      orderBy,
      orderDir,
      pageNum,
      count: itemCountPerPage,
    })
      .catch(() => ({ results: [], totalCount: 0, categories: [], totals: null }))
      .then((res) => {
        setRows(res?.results ?? [])
        setTotalCount(res?.totalCount ?? 0)
        setTotals(res?.totals ?? null)
        // Options come off the unfiltered set, so keep the last non-empty list.
        if (res?.categories?.length) setCategories(res.categories)
      })
      .finally(() => setLoading(false))
  }, [debouncedQuery, category, orderBy, orderDir, pageNum, itemCountPerPage])

  const onSort = (key: string) => {
    if (orderBy === key) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(key)
      // A newly picked column starts descending too, matching the date column it replaces.
      setOrderDir('desc')
    }
    setPageNum(0)
  }

  const exportUrl = useMemo(() => {
    const params = new URLSearchParams({ token })
    if (debouncedQuery) params.set('query', debouncedQuery)
    if (category) params.set('category', category)
    params.set('orderBy', orderBy)
    params.set('orderDir', orderDir)
    return `${baseUrl}/loan/downloadTrustLedgerAll/${loanNumber}?${params.toString()}`
  }, [token, loanNumber, debouncedQuery, category, orderBy, orderDir])

  const onExportExcel = () => {
    const windowReference: any = window.open()
    windowReference.document.title = 'Downloading...'
    windowReference.location = exportUrl
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <div className="flex justify-between items-end flex-wrap gap-2">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="font-variation-settings-600 text-lg pb-1">- All Transactions</div>
          <div className="w-64">
            <Input2
              type="search"
              title="Search all columns"
              value={query}
              onChange={(value: string) => setQuery(value)}
            />
          </div>
          <select
            className="text-[14px] border rounded px-2 py-2 mb-1"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value)
              setPageNum(0)
            }}
          >
            <option value="">All Categories</option>
            {categories.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
          <span className="text-[13px] text-gray-500 pb-2">
            {totalCount} transaction{totalCount === 1 ? '' : 's'}
          </span>
        </div>
        <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={onExportExcel}>
          <div className="flex items-center">
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" /> Export to Excel
          </div>
        </button>
      </div>

      <div className="mt-2 overflow-auto shadow-md rounded">
        <table className="w-full text-[14px]">
          <thead className="font-medium text-gray-600 bg-gray-100">
            <tr className="text-left">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2 cursor-pointer hover:text-shade-blue select-none ${col.align || ''}`}
                  onClick={() => onSort(col.key)}
                >
                  <span className={`inline-flex items-center gap-1 ${col.align === 'text-right' ? 'flex-row-reverse' : ''}`}>
                    {col.title}
                    {orderBy === col.key &&
                      (orderDir === 'asc' ? (
                        <ChevronUpIcon className="w-3 h-3" />
                      ) : (
                        <ChevronDownIcon className="w-3 h-3" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.categoryKey}-${row.id}-${index}`} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span
                    className={`rounded px-2 py-0.5 text-[12px] ${
                      TRUST_LEDGER_CATEGORY_COLORS[row.categoryKey] || 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {row.category}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">{row.trustAccountName || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.date ? formatDate(row.date) : ''}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.type}</td>
                <td className="px-4 py-2">{row.reference}</td>
                <td className="px-4 py-2">{row.fromWhom}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.payment)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.deposit)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.fees)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.balance)}</td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={COLUMNS.length}>
                  No transactions
                </td>
              </tr>
            )}
          </tbody>
          {!!rows.length && totals && (
            <TotalsRow
              tdClass="px-4 py-2"
              cells={[
                'Totals',
                '',
                '',
                '',
                '',
                '',
                totals.payment,
                totals.deposit,
                totals.fees,
                '',
              ]}
            />
          )}
        </table>
      </div>

      <div className="flex justify-end items-center mt-3 mb-3">
        <Pagination
          totalCount={totalCount}
          itemCountPerPage={itemCountPerPage}
          onNavigate={setPageNum}
          pageNum={pageNum}
          onCountPerPageUpdate={(count: number) => {
            setItemCountPerPage(count)
            setPageNum(0)
          }}
        />
      </div>
    </div>
  )
}
